import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ErrorBoundary } from 'react-error-boundary';
import {
    Button,
    BUTTON_VARIANTS,
    Icon,
    ScrollAreaPrimitive,
    ScrollAreaRoot,
    ScrollAreaViewport,
    ScrollBar,
    SCROLLAREA_VARIANTS
} from 'afl-design-system';

import { MatchCard } from 'common/react/components/match-card/MatchCard';
import { MatchCardByes } from 'common/react/components/match-card/MatchCardByes';
import { useWindowSize } from 'common/react/hooks/use-window-resize';
import { getTranslation } from 'common/react/utils/translations';

import { RoundHeader } from './RoundHeader';
import { useScrollControls } from './useScrollControls';

const { CAROUSEL } = PULSE.app.common.CONSTANTS.TRACKING.EVENTS;

const SLIDER_NAME = 'fixture-bar';

// Gap added to the widest header's width to keep keyboard-focused cards clear of it.
const HEADER_GUTTER_PADDING = 8;

// New translation keys not yet in the CMS, so each carries an English fallback.
const ARROW_LABELS = {
    PREVIOUS: {
        key: 'label.fixtureBar.previousMatches',
        fallback: 'Previous matches'
    },
    NEXT: { key: 'label.fixtureBar.nextMatches', fallback: 'Next matches' }
};

const getArrowLabel = ({ key, fallback }) => getTranslation(key) ?? fallback;

// Buckets matches into one group per round; round metadata comes from the first
// match seen. Sorting is defensive - the API already returns round order.
const groupMatchesByRound = (matches) => {
    const groups = new Map();

    matches.forEach((match) => {
        const round = match.round;

        if (!round) {
            return;
        }

        if (!groups.has(round.roundNumber)) {
            groups.set(round.roundNumber, {
                roundNumber: round.roundNumber,
                round,
                matches: [],
                byes: round.byes ?? []
            });
        }

        groups.get(round.roundNumber).matches.push(match);
    });

    return [...groups.values()]
        .sort((a, b) => a.roundNumber - b.roundNumber)
        .map((group) => ({
            ...group,
            // ISO 8601 strings sort chronologically as text.
            matches: [...group.matches].sort((a, b) =>
                (a.utcStartTime ?? '').localeCompare(b.utcStartTime ?? '')
            )
        }));
};

// The horizontally scrolling rail of match cards, one sticky header per round.
// Each round is its own group with its header as the first child, so
// `position: sticky` hands off from one header to the next as rounds scroll by.
// Composed from DS primitives rather than the `ScrollArea` wrapper, which
// hardcodes `gap-s` and can't give rounds/cards their different gaps.
export const MatchBar = ({ matches, allMatchesLink, isFinalsMode = false }) => {
    const windowSize = useWindowSize();
    const isDesktop = windowSize.width >= PULSE.app.measurements.desktop;

    const groups = useMemo(() => groupMatchesByRound(matches), [matches]);

    const {
        containerRef,
        viewportRef,
        getViewport,
        canScrollPrev,
        canScrollNext,
        stuckRoundNumber,
        page
    } = useScrollControls();

    const [headerGutter, setHeaderGutter] = useState(0);

    const allMatchesText = getTranslation('label.fixtureBar.allMatches');

    // Keyboard focus scrolls an off-screen card flush to the left edge, which
    // would land it under the sticky header - measure the widest header and pad
    // the scroll area by that.
    useEffect(() => {
        const viewport = getViewport();

        if (!viewport) {
            return;
        }

        const widths = [
            ...viewport.querySelectorAll('.fixture-bar__round')
        ].map((header) => header.offsetWidth);

        setHeaderGutter(
            widths.length ? Math.max(...widths) + HEADER_GUTTER_PADDING : 0
        );
    }, [getViewport, groups, isDesktop]);

    // Pages the rail and records the same carousel event the old Swiper build
    // sent, so the arrows stay comparable in analytics.
    const handlePage = useCallback(
        (direction) => {
            const target = page(direction);
            const viewport = getViewport();
            const slides = viewport
                ? [...viewport.querySelectorAll('.fixture-bar__slide')]
                : [];

            // Names the card being scrolled to rather than the current left-edge
            // card, since the smooth scroll hasn't moved yet at this point.
            const firstVisible =
                target === null
                    ? -1
                    : slides.findIndex(
                          (slide) => slide.offsetLeft >= target - 1
                      );

            PULSE.app.tracking.recordEvent(CAROUSEL.EVENT_NAME, {
                eventData: {
                    /* eslint-disable camelcase */
                    component_name: SLIDER_NAME,
                    component_content_type: CAROUSEL.CONTENT_TYPE.FIXTURE,
                    /* eslint-enable camelcase */
                    count: slides.length,
                    index: firstVisible + 1,
                    type: CAROUSEL.TYPE.ARROW,
                    direction:
                        direction > 0
                            ? CAROUSEL.DIRECTION.NEXT
                            : CAROUSEL.DIRECTION.PREVIOUS
                }
            });
        },
        [page, getViewport]
    );

    return (
        <div
            ref={containerRef}
            className="fixture-bar__matches-container flex min-w-0 flex-1 items-center gap-xs"
        >
            <ScrollAreaRoot
                variant={SCROLLAREA_VARIANTS.VARIANT.HORIZONTAL}
                className="min-w-0 flex-1 [--scroll-area-mask-size-end:var(--spacing-5xl)]"
            >
                <ScrollAreaViewport
                    ref={viewportRef}
                    variant={SCROLLAREA_VARIANTS.VARIANT.HORIZONTAL}
                    overflowStart={false}
                    overflowEnd={isDesktop && canScrollNext}
                    className={isDesktop ? 'pb-0' : ''}
                    style={{ scrollPaddingInlineStart: headerGutter }}
                >
                    <ScrollAreaPrimitive.Content
                        className={`flex flex-row items-center py-xs gap-xs ${
                            isDesktop ? '' : 'mr-m pl-m'
                        }`}
                    >
                        {groups.map((group) => (
                            <div
                                key={group.roundNumber}
                                data-round-number={group.roundNumber}
                                className={`flex shrink-0 ${
                                    isDesktop
                                        ? 'flex-row items-center gap-xs'
                                        : 'flex-col items-start gap-2xs'
                                }`}
                            >
                                <RoundHeader
                                    round={group.round}
                                    isStacked={!isDesktop}
                                    isPinned={
                                        isDesktop &&
                                        group.roundNumber === stuckRoundNumber
                                    }
                                    isFinalsMode={isFinalsMode}
                                />
                                <div className="flex shrink-0 flex-row items-center gap-xs">
                                    {group.matches.map((match) => (
                                        <div
                                            key={match.id}
                                            className="fixture-bar__slide flex w-160 shrink-0 items-center l:w-auto"
                                        >
                                            <ErrorBoundary fallback={<></>}>
                                                <MatchCard
                                                    matchInfo={match}
                                                    layout={
                                                        isDesktop
                                                            ? 'desktop'
                                                            : 'mobile'
                                                    }
                                                />
                                            </ErrorBoundary>
                                        </div>
                                    ))}
                                    {group.byes.length > 0 && (
                                        <div className="fixture-bar__slide flex w-160 shrink-0 items-center l:w-auto">
                                            <ErrorBoundary fallback={<></>}>
                                                <MatchCardByes
                                                    byes={group.byes}
                                                />
                                            </ErrorBoundary>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        <div
                            className={`flex shrink-0 ${
                                isDesktop
                                    ? 'flex-row items-center gap-xs'
                                    : 'flex-col items-start gap-2xs self-stretch'
                            }`}
                        >
                            {!isDesktop && (
                                <div
                                    className={`flex shrink-0 items-center py-3xs pl-m`}
                                >
                                    <span className="body-s-semi-bold">
                                        &nbsp;
                                    </span>
                                </div>
                            )}
                            <div
                                className="flex flex-row items-center gap-xs h-full"
                                data-theme={isFinalsMode ? 'finals' : null}
                            >
                                <Button
                                    // `!` needed: unlayered `a { background-color:
                                    // transparent }` in _normalize.scss otherwise
                                    // outranks the DS's `@layer` fill regardless of specificity.
                                    className={`fixture-bar__all-matches shrink-0 bg-secondary! hover:bg-state-secondary-hovered! focus:bg-state-secondary-focussed! ml-xs mr-m`}
                                    variant={BUTTON_VARIANTS.VARIANT.SECONDARY}
                                    size={BUTTON_VARIANTS.SIZE.SMALL}
                                    render={
                                        <a
                                            href={allMatchesLink}
                                            target="_blank"
                                            rel="noreferrer"
                                            aria-label={allMatchesText}
                                        />
                                    }
                                >
                                    {allMatchesText}
                                </Button>
                            </div>
                        </div>
                    </ScrollAreaPrimitive.Content>
                </ScrollAreaViewport>
                {!isDesktop && (
                    <ScrollBar
                        orientation={SCROLLAREA_VARIANTS.VARIANT.HORIZONTAL}
                    />
                )}
            </ScrollAreaRoot>
            {isDesktop && (
                <div className="flex shrink-0 items-center gap-xs pr-l">
                    <Button
                        className="fixture-bar__carousel-button fixture-bar__carousel-button--prev"
                        variant={BUTTON_VARIANTS.VARIANT.SECONDARY}
                        size={BUTTON_VARIANTS.SIZE.SMALL}
                        iconOnly
                        disabled={!canScrollPrev}
                        onClick={() => handlePage(-1)}
                        aria-label={getArrowLabel(ARROW_LABELS.PREVIOUS)}
                    >
                        <Icon name="arrow-back" />
                    </Button>
                    <Button
                        className="fixture-bar__carousel-button fixture-bar__carousel-button--next"
                        variant={BUTTON_VARIANTS.VARIANT.SECONDARY}
                        size={BUTTON_VARIANTS.SIZE.SMALL}
                        iconOnly
                        disabled={!canScrollNext}
                        onClick={() => handlePage(1)}
                        aria-label={getArrowLabel(ARROW_LABELS.NEXT)}
                    >
                        <Icon name="arrow-next" />
                    </Button>
                </div>
            )}
        </div>
    );
};

MatchBar.propTypes = {
    matches: PropTypes.array.isRequired,
    allMatchesLink: PropTypes.string.isRequired,
    isFinalsMode: PropTypes.bool
};
